import { useState, useEffect, useMemo } from 'react';
import {
  collection, onSnapshot, doc, runTransaction, serverTimestamp,
  type Transaction, type Firestore, type DocumentReference,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { logActivity } from '../utils/activity';
import AppLayout from '../components/AppLayout';
import { useItemParam } from '../hooks/useNewParam';
import { Button, Modal, ModalBanner, ConfirmModal, FormField, DeleteIconButton, EntityCombobox, TotalsFigures } from '../components/ui';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { useColumnFilters, clarityCompare, type FilterColumn } from '../hooks/useColumnFilters';
import { useGridKeyNav } from '../hooks/useGridKeyNav';
import { HeaderSearch, DateRangeButton, StatChips, IconPlusSm } from '../components/HeaderControls';
import { useSettings, settingsLoaded } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import {
  buildEntry, postEntry, entryId, linesStockRevaluation,
} from '../utils/ledger';
import type { InventoryItem, StockRevaluation, Sku } from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
// createdAt is written via serverTimestamp() (a Firestore Timestamp once
// synced back) even though the app's own types declare it as `string` — a
// plain .localeCompare() on it throws and crashes the page the moment two
// rows share the same date and the sort's tie-breaker actually runs. Reads
// either shape (Timestamp instance, plain {seconds}, or an ISO string)
// into sortable millis instead. Mirrors Dashboard.tsx's own toMillis.
function toMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') return new Date(v).getTime();
  const obj = v as { toMillis?: () => number; seconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') return obj.seconds * 1000;
  return 0;
}
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const availCarat = (i: InventoryItem) => round2((i.carat ?? 0) - (i.memoCommittedCarat ?? 0));
const onMemoCt = (i: InventoryItem) => round2(Math.max(0, i.memoCommittedCarat ?? 0));
const costPerCt = (i: InventoryItem) => ((i.carat ?? 0) > 0 ? round2((i.costUSD ?? 0) / (i.carat as number)) : 0);

const PAGE_SIZE = 50;

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity/Memos/AP/AR/SalesReturns) ──
function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> revaluation{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

// ── Revalue modal ───────────────────────────────────────────────────────
// Same normalization as Purchase / Inventory: keys itemCodes/{codeId}/history.
function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}

/**
 * Writes one stone's revaluation (write phase only; the caller reads the
 * item and its SKU first). Used by Revalue Stock (one stone) and Revalue
 * Many (a whole grid), so both save exactly the same records: the stone's
 * new cost (Price = Cost after a revaluation), the SKU benchmark + its
 * history line, the stone's "Revalued" history line, the revaluation doc
 * and its journal entry (Inventory Adjustment Gain / Loss, so Profit & Loss
 * shows it). `skuPrior` = the SKU's per ct before this write (null when it
 * has none yet, undefined when the stone has no SKU doc). Returns the figures for the caller.
 */
function writeRevaluation(tx: Transaction, db: Firestore, ctx: {
  businessId: string; userId: string; currency: string; date: string;
  reason: string; notes?: string; batch?: { id: string; no: string };
}, a: {
  itemId: string; cur: InventoryItem; np: number; revRef: DocumentReference; revNo: string; skuPrior?: number | null;
}) {
  const { cur, np, revRef } = a;
  const carat = cur.carat ?? 0;
  const oldCostUSD = round2(cur.costUSD ?? 0);
  const oldCostingUSD = round2(cur.costingUSD ?? 0);
  const oldPerCt = carat > 0 ? round2(oldCostUSD / carat) : 0;
  const newCostUSD = round2(np * carat);
  const newCostingUSD = newCostUSD;                 // reval collapses the added-value overlay to 0
  const deltaUSD = round2(newCostUSD - oldCostUSD);

  tx.update(doc(db, 'inventory', a.itemId), { costUSD: newCostUSD, costingUSD: newCostingUSD, updatedAt: serverTimestamp() });

  if (cur.skuId && a.skuPrior !== undefined) {
    tx.update(doc(db, 'skus', cur.skuId), {
      currentAvgCostPerCt: np, currentAddedValuePercent: 0, currentCostingPerCt: np,
      lastActivityAt: ctx.date, updatedAt: serverTimestamp(),
    });
    // Deterministic id so a later delete can remove exactly this entry.
    tx.set(doc(db, 'skus', cur.skuId, 'history', `reval_${revRef.id}`), stripUndef({
      type: 'revaluation' as const, date: ctx.date,
      revaluationId: revRef.id, revaluedInventoryItemId: a.itemId,
      previousAvgCostPerCt: a.skuPrior ?? oldPerCt, newAvgCostPerCt: np,
      createdAt: serverTimestamp(), createdBy: ctx.userId,
    }));
  }

  // A "Revalued" line in the stone's history, so the Inventory popup
  // shows it and restarts Price/ct and Residual Cost/ct from it.
  if (cur.code) {
    tx.set(doc(db, 'itemCodes', normalizeCode(cur.code), 'history', `reval_${revRef.id}`), stripUndef({
      type: 'revaluation', date: ctx.date, carat,
      pricePerCt: np, amountUSD: newCostUSD,
      oldPricePerCt: carat > 0 ? round2(oldCostingUSD / carat) : 0,
      revaluationId: revRef.id, inventoryItemId: a.itemId,
      notes: ctx.reason || undefined,
      createdAt: serverTimestamp(), createdBy: ctx.userId,
    }));
  }

  tx.set(revRef, stripUndef({
    businessId: ctx.businessId, revaluationNo: a.revNo, date: ctx.date,
    inventoryItemId: a.itemId, code: cur.code || undefined,
    sku: cur.sku || undefined, skuId: cur.skuId || undefined,
    shape: cur.shape, carat,
    oldCostPerCt: oldPerCt, newCostPerCt: np,
    oldCostUSD, newCostUSD,
    deltaUSD, oldCostingUSD, newCostingUSD,
    reason: ctx.reason, notes: ctx.notes || undefined,
    batchId: ctx.batch?.id, batchNo: ctx.batch?.no,
    createdAt: new Date().toISOString(), createdBy: ctx.userId,
  }));

  postEntry(tx, db, buildEntry({
    businessId: ctx.businessId, sourceType: 'stock_revaluation', sourceId: revRef.id,
    sourceCollection: 'stockRevaluations', date: ctx.date, currency: ctx.currency,
    createdBy: ctx.userId, note: `Revaluation: ${ctx.reason}`,
    lines: linesStockRevaluation(deltaUSD),
  }), entryId('stock_revaluation', revRef.id));

  return { carat, deltaUSD, oldPerCt, oldCostUSD, newCostUSD };
}

function RevalueModal({ items, onClose, initialItemId }: { items: InventoryItem[]; onClose: () => void; initialItemId?: string }) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [itemId, setItemId] = useState(initialItemId ?? '');
  const [date, setDate] = useState(todayISO());
  const [newPerCt, setNewPerCt] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const item = items.find(i => i.id === itemId) ?? null;
  // Type to find a stone by Code, shape, carat or grade (same picker as Company).
  const itemOptions = useMemo(() => items.map(i => ({
    id: i.id,
    // Full carats: a revaluation covers the stones on memo too.
    companyName: `${i.code ? `${i.code} · ` : ''}${i.shape} ${(i.carat ?? 0).toFixed(2)}ct${onMemoCt(i) > 0 ? ` (${onMemoCt(i).toFixed(2)} on memo)` : ''} ${i.color} ${i.clarity} · ${money(costPerCt(i))}/ct`,
  })), [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const oldPerCt = item ? costPerCt(item) : 0;
  const np = parseFloat(newPerCt);
  const preview = useMemo(() => {
    if (!item || isNaN(np) || np < 0) return null;
    const newCostUSD = round2(np * (item.carat ?? 0));
    return { newCostUSD, delta: round2(newCostUSD - (item.costUSD ?? 0)) };
  }, [item, np]);

  async function save() {
    if (!item) return setError('Pick an item.');
    if (isNaN(np) || np < 0) return setError('Enter the new cost per carat.');
    if (!reason.trim()) return setError('Enter a reason.');
    if (Math.abs((preview?.delta ?? 0)) < 0.005) return setError('That is the same value it already carries.');

    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }

    const prefix = (settings.stockRevaluationPrefix || 'RV').toUpperCase();
    const revRef = doc(collection(db, 'stockRevaluations'));
    const itemRef = doc(db, 'inventory', item.id);
    const counterRef = doc(db, 'counters', business.id);
    const skuId = item.skuId;
    const skuRef = skuId ? doc(db, 'skus', skuId) : null;

    try {
      await runTransaction(db, async tx => {
        const [cs, is, ss] = await Promise.all([
          tx.get(counterRef), tx.get(itemRef), skuRef ? tx.get(skuRef) : Promise.resolve(null),
        ]);
        if (!is.exists()) throw new Error('That item no longer exists.');
        const cur = is.data() as InventoryItem;
        if (cur.status !== 'in-stock') throw new Error('That item is no longer in stock.');

        const n = (cs.exists() ? (cs.data().stockRevaluation ?? 0) : 0) + 1;
        const revNo = `${prefix}-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`;
        tx.set(counterRef, { stockRevaluation: n }, { merge: true });

        const r = writeRevaluation(tx, db, {
          businessId: business.id, userId: user!.id, currency: settings.currency, date,
          reason: reason.trim(), notes: notes.trim() || undefined,
        }, {
          itemId: item.id, cur, np, revRef, revNo,
          skuPrior: ss?.exists() ? ((ss.data() as Sku).currentAvgCostPerCt ?? null) : undefined,
        });

        // Activity Log: who revalued which stone, from what to what.
        logActivity(tx, db, business.id, user, {
          module: 'revaluation', action: 'create', recordId: revRef.id, recordNo: revNo,
          itemCount: 1, carat: r.carat, amountUSD: r.deltaUSD,
          note: `Code ${cur.code || '—'} · ${money(r.oldPerCt)} to ${money(np)} per ct · ${reason.trim()}`,
        });
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the revaluation.');
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} hideHead
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>Save Revaluation</Button>
      </>}>
      <ModalBanner title={item?.code || 'Revalue Stock'} eyebrow="Revalue Stock" onClose={onClose} />

      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

      <FormField label="Item" required>
        <EntityCombobox
          entities={itemOptions} value={itemId}
          onChange={id => { setItemId(id); setNewPerCt(''); }}
          placeholder="Type a Code, shape or grade…"
        />
      </FormField>
      {item && (
        <div className="rv-cover">
          {onMemoCt(item) > 0
            ? <>Revalues all <strong>{(item.carat ?? 0).toFixed(2)} ct</strong>: {availCarat(item).toFixed(2)} ct in stock and {onMemoCt(item).toFixed(2)} ct out on memo.</>
            : <>Revalues all <strong>{(item.carat ?? 0).toFixed(2)} ct</strong> in stock.</>}
        </div>
      )}

      <div className="form-row-2">
        <FormField label={`Current cost / ct (${settings.currency})`}>
          <input value={item ? String(oldPerCt) : ''} disabled readOnly />
        </FormField>
        <FormField label={`New cost / ct (${settings.currency})`} required>
          <input type="number" step="0.01" min="0" className="no-spinner" value={newPerCt}
            onChange={e => setNewPerCt(e.target.value)} placeholder="0.00" />
        </FormField>
      </div>
      <div className="form-row-2">
        <FormField label="Date" required>
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
        <FormField label="Reason" required>
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. market softened, over-valued at purchase" />
        </FormField>
      </div>
      <FormField label="Notes (optional)">
        <input value={notes} onChange={e => setNotes(e.target.value)} />
      </FormField>

      {preview && item && (
        <div style={{ marginTop: 6, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', fontSize: 13 }}>
          Carrying value {money(item.costUSD ?? 0)} → <strong>{money(preview.newCostUSD)}</strong>
          {'  ·  '}
          <strong style={{ color: preview.delta < 0 ? 'var(--danger)' : 'var(--success)' }}>
            {preview.delta < 0 ? 'write-down ' : 'write-up '}{money(Math.abs(preview.delta))}
          </strong>
          {item.skuId ? <span style={{ color: 'var(--text-3)' }}>{'  ·  SKU '}{item.sku} re-benchmarked to {money(np)}/ct</span> : null}
        </div>
      )}
    </Modal>
  );
}

// ── Revalue Many: an Excel style grid over the whole stock ──────────────
// One row per stone. New Cost/ct and % Change are two views of one figure:
// type either and the other follows (the one typed last wins). "Apply to
// all" puts one % on the ticked rows (or every row shown when none are
// ticked). Only rows that change are saved, each as its own revaluation
// (same records as Revalue Stock, so Item History and Delete work as
// before), all sharing one batch number. Saved in chunks so a big run
// stays inside Firestore's per transaction limits.
type RowEdit = { mode: 'cost' | 'pct'; value: string };
const CHUNK = 50;

/** Stones a revaluation makes sense for: owned, in stock, with carats. */
const revaluable = (i: InventoryItem, dummyId?: string) =>
  i.status === 'in-stock' && (i.carat ?? 0) > 0 && i.provenance !== 'memo-in'
  && i.category !== 'books-dummy' && i.id !== dummyId;

const stoneText = (i: InventoryItem) => [i.shape, i.size, i.color, i.clarity].filter(Boolean).join(' ');

function BulkRevalueModal({ items, onClose, onSaved }: {
  items: InventoryItem[]; onClose: () => void; onSaved: (batchNo: string, count: number) => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [date, setDate] = useState(todayISO());
  const [reason, setReason] = useState('');
  const [allPct, setAllPct] = useState('');
  const [search, setSearch] = useState('');
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');

  const stock = useMemo(() => items
    .filter(i => revaluable(i, settings.dummyInventoryItemId))
    .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? '', undefined, { numeric: true })),
  [items, settings.dummyInventoryItemId]);

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? stock.filter(i => [i.code, i.sku, stoneText(i), i.lab].some(v => (v ?? '').toLowerCase().includes(q))) : stock;
  }, [stock, search]);
  const filterCols = useMemo<FilterColumn<InventoryItem>[]>(() => [
    { key: 'sku',     label: 'SKU',     get: i => i.sku },
    { key: 'shape',   label: 'Shape',   get: i => i.shape },
    { key: 'size',    label: 'Size',    get: i => i.size },
    { key: 'color',   label: 'Color',   get: i => i.color },
    { key: 'clarity', label: 'Clarity', get: i => i.clarity, sort: clarityCompare },
    { key: 'lab',     label: 'Lab',     get: i => i.lab || 'Loose' },
  ], []);
  const { rows: shown, filters } = useColumnFilters(searched, filterCols);

  // A row's new cost / ct and % change, from whichever box was typed.
  const calc = (i: InventoryItem) => {
    const old = costPerCt(i);
    const e = edits[i.id];
    const n = e ? parseFloat(e.value) : NaN;
    if (!e || isNaN(n)) return { old, np: null as number | null, pct: null as number | null };
    if (e.mode === 'cost') return { old, np: n, pct: old > 0 ? round2((n / old - 1) * 100) : null };
    return { old, np: round2(old * (1 + n / 100)), pct: n };
  };
  const changeOf = (i: InventoryItem, np: number | null) =>
    np == null ? 0 : round2(np * (i.carat ?? 0) - (i.costUSD ?? 0));

  const changed = useMemo(() => stock
    .map(i => ({ i, ...calc(i) }))
    .filter(r => r.np != null && r.np >= 0 && Math.abs(changeOf(r.i, r.np)) >= 0.005),
  [stock, edits]); // eslint-disable-line react-hooks/exhaustive-deps
  const bad = stock.some(i => { const c = calc(i); return c.np != null && c.np < 0; });

  const tot = useMemo(() => {
    let ct = 0, cur = 0, next = 0;
    for (const r of changed) { ct += r.i.carat ?? 0; cur += r.i.costUSD ?? 0; next += round2(r.np! * (r.i.carat ?? 0)); }
    return { ct: round2(ct), cur: round2(cur), next: round2(next), change: round2(next - cur) };
  }, [changed]);

  const setEdit = (id: string, mode: RowEdit['mode'], value: string) =>
    setEdits(e => {
      const n = { ...e };
      if (value.trim() === '') delete n[id]; else n[id] = { mode, value };
      return n;
    });
  const targets = () => { const t = shown.filter(i => ticked.has(i.id)); return t.length ? t : shown; };
  function applyAll() {
    const p = parseFloat(allPct);
    if (isNaN(p) || p <= -100) { setError('Enter a % change above -100, e.g. -5 or 3.'); return; }
    setError('');
    setEdits(e => { const n = { ...e }; for (const i of targets()) n[i.id] = { mode: 'pct', value: String(p) }; return n; });
  }
  const allTicked = shown.length > 0 && shown.every(i => ticked.has(i.id));
  const toggleAll = () => setTicked(t => {
    const n = new Set(t);
    if (allTicked) shown.forEach(i => n.delete(i.id)); else shown.forEach(i => n.add(i.id));
    return n;
  });

  const nav = useGridKeyNav(shown.length, 2);
  const dirty = Object.keys(edits).length > 0;
  const close = () => { if (saving) return; if (dirty && !window.confirm('Discard these new costs?')) return; onClose(); };

  async function save() {
    if (!reason.trim()) return setError('Enter a reason.');
    if (bad) return setError('A new cost can\'t be below 0.');
    if (changed.length === 0) return setError('Type a new cost or a % on at least one item.');
    const db = getClientDb();
    if (!db || !business || !user) return setError('Not connected.');
    setSaving(true); setError('');

    const prefix = (settings.stockRevaluationPrefix || 'RV').toUpperCase();
    const counterRef = doc(db, 'counters', business.id);
    const batchId = doc(collection(db, 'stockRevaluations')).id;
    let batchNo = '';
    const work = changed.map(r => ({ id: r.i.id, np: r.np! }));
    const savedIds: string[] = [];
    let sumCt = 0, sumDelta = 0;

    try {
      for (let start = 0; start < work.length; start += CHUNK) {
        const part = work.slice(start, start + CHUNK);
        const first = start === 0;
        const last = start + CHUNK >= work.length;
        setProgress(`Saving ${Math.min(start + CHUNK, work.length)} of ${work.length}…`);
        const revRefs = part.map(() => doc(collection(db, 'stockRevaluations')));
        // Filled by the transaction's last (successful) run: it can re-run.
        let result = { ids: [] as string[], ct: 0, delta: 0, batchNo };
        await runTransaction(db, async tx => {
          // ── Reads ──
          const cs = await tx.get(counterRef);
          const snaps = await Promise.all(part.map(w => tx.get(doc(db, 'inventory', w.id))));
          const skuIds = [...new Set(snaps.map(s => (s.exists() ? (s.data() as InventoryItem).skuId : undefined)).filter((x): x is string => !!x))];
          const skuSnaps = await Promise.all(skuIds.map(id => tx.get(doc(db, 'skus', id))));
          // Running per ct of each SKU: two stones of one SKU in this run
          // chain their history lines (the second starts where the first ended).
          const skuPrior = new Map<string, number | null>();
          skuSnaps.forEach((s, k) => { if (s.exists()) skuPrior.set(skuIds[k], (s.data() as Sku).currentAvgCostPerCt ?? null); });

          // ── Writes ──
          const c = (cs.exists() ? cs.data() : {}) as { stockRevaluation?: number; stockRevaluationBatch?: number };
          let n = c.stockRevaluation ?? 0;
          const batchN = first ? (c.stockRevaluationBatch ?? 0) + 1 : undefined;
          const no = batchN != null
            ? `${prefix}B-${new Date().getFullYear()}-${String(batchN).padStart(4, '0')}`
            : batchNo;
          const ctx = {
            businessId: business.id, userId: user.id, currency: settings.currency, date,
            reason: reason.trim(), batch: { id: batchId, no },
          };
          let ct = 0, delta = 0;
          const ids: string[] = [];
          part.forEach((w, k) => {
            const s = snaps[k];
            if (!s.exists()) return;
            const cur = s.data() as InventoryItem;
            if (cur.status !== 'in-stock') return;          // sold or moved since: skipped
            n += 1;
            const revNo = `${prefix}-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`;
            const hasSku = !!cur.skuId && skuPrior.has(cur.skuId);
            const r = writeRevaluation(tx, db, ctx, {
              itemId: w.id, cur, np: w.np, revRef: revRefs[k], revNo,
              skuPrior: hasSku ? skuPrior.get(cur.skuId!)! : undefined,
            });
            if (hasSku) skuPrior.set(cur.skuId!, w.np);
            ct += r.carat; delta += r.deltaUSD; ids.push(w.id);
          });
          // One write to the counter doc per transaction.
          tx.set(counterRef, { stockRevaluation: n, ...(batchN != null ? { stockRevaluationBatch: batchN } : {}) }, { merge: true });
          // One Activity Log line for the whole run, with the last chunk.
          if (last) {
            logActivity(tx, db, business.id, user, {
              module: 'revaluation', action: 'create', recordId: batchId, recordNo: no,
              itemCount: savedIds.length + ids.length, carat: round2(sumCt + ct), amountUSD: round2(sumDelta + delta),
              note: `Revalue Many · ${reason.trim()}`,
            });
          }
          result = { ids, ct, delta, batchNo: no };
        });
        batchNo = result.batchNo;
        savedIds.push(...result.ids); sumCt += result.ct; sumDelta += result.delta;
      }
      setProgress('');
      onSaved(batchNo, savedIds.length);
      onClose();
    } catch (e) {
      // Stones already saved keep their new cost: take them off the grid so
      // running Save again can't revalue them twice.
      setEdits(prev => { const n = { ...prev }; savedIds.forEach(id => delete n[id]); return n; });
      setProgress('');
      setError(`${savedIds.length ? `Saved ${savedIds.length} of ${work.length} items (batch ${batchNo}). ` : ''}${e instanceof Error ? e.message : 'Could not save.'} The rest were not changed; Save again for them.`);
    }
    setSaving(false);
  }

  // Plain number: a number box can't show a leading "+" (it would show empty).
  const pctText = (p: number | null) => (p == null ? '' : p.toFixed(2));

  return (
    <Modal open onClose={close} size="full" hideHead
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', flexWrap: 'wrap' }}>
          <TotalsFigures figures={[
            { label: 'Items', value: String(changed.length) },
            { label: 'Carat', value: tot.ct.toFixed(2) },
            { label: 'Current', value: money(tot.cur) },
            { label: 'New', value: money(tot.next) },
            { label: 'Change', value: `${tot.change > 0 ? '+' : ''}${money(tot.change)}`, strong: true,
              color: tot.change < 0 ? 'var(--danger)' : tot.change > 0 ? 'var(--success)' : undefined },
          ]} />
          {progress && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{progress}</span>}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={close} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving} disabled={changed.length === 0}>
              {changed.length ? `Revalue ${changed.length} Item${changed.length === 1 ? '' : 's'}` : 'Revalue'}
            </Button>
          </div>
        </div>
      }>
      <ModalBanner title="Revalue Many" eyebrow="Stock Revaluation" onClose={close} />
      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

      {/* One row: Date · Reason · Search · Apply % (all 38px). */}
      <div className="rvm-top">
        <FormField label="Date" required>
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
        <FormField label="Reason" required>
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. market softened" />
        </FormField>
        <div className="form-group rvm-search">
          <label>Search</label>
          <HeaderSearch value={search} onChange={setSearch} placeholder="Code, SKU, shape, grade…" label="Search items" />
        </div>
        <div className="form-group rvm-apply">
          <label>Apply % to {ticked.size && shown.some(i => ticked.has(i.id)) ? 'ticked' : 'all shown'}</label>
          <div className="rvm-apply-row">
            <input type="number" step="0.01" className="no-spinner" value={allPct} placeholder="-5"
              onChange={e => setAllPct(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyAll(); } }} />
            <span className="rvm-pct">%</span>
            <Button variant="secondary" onClick={applyAll} disabled={shown.length === 0}>Apply</Button>
            {dirty && <button type="button" className="tb-clear" onClick={() => setEdits({})}>Clear all</button>}
          </div>
        </div>
      </div>

      <div className="rvm-bar">
        <span className="rvm-count">{shown.length} of {stock.length} items</span>
        <ColumnFilterChips filters={filters} inline />
        {filters.active.length > 0 && <button type="button" className="tb-clear" onClick={filters.clear}>Clear filters</button>}
      </div>

      {/* Wait for the company's Settings, so amounts show in its own currency
          (not the USD fallback for a moment). */}
      {!settingsLoaded(settings) ? (
        <div className="skeleton" style={{ height: 260, borderRadius: 8 }} />
      ) : (
      <div className="rvm-grid">
        <table className="excel-grid-table">
          <thead>
            <tr>
              <th style={{ width: 34, textAlign: 'center' }}>
                <input type="checkbox" checked={allTicked} onChange={toggleAll} aria-label="Tick all shown" />
              </th>
              <th style={{ width: 70 }}>Code</th>
              <th className="th-filterable" style={{ width: 110 }}>SKU<ColumnFilterButton filters={filters} colKey="sku" label="SKU" /></th>
              <th className="th-filterable">Item<ColumnFilterButton filters={filters} colKey="shape" label="Shape" /></th>
              <th className="th-filterable" style={{ width: 60 }}>Color<ColumnFilterButton filters={filters} colKey="color" label="Color" /></th>
              <th className="th-filterable" style={{ width: 66 }}>Clarity<ColumnFilterButton filters={filters} colKey="clarity" label="Clarity" /></th>
              <th className="th-filterable" style={{ width: 56 }}>Lab<ColumnFilterButton filters={filters} colKey="lab" label="Lab" /></th>
              <th style={{ width: 110, textAlign: 'right' }} title="All carats: in stock and out on memo">Carat</th>
              <th style={{ width: 96, textAlign: 'right' }}>Cost/ct</th>
              <th style={{ width: 104, textAlign: 'right' }}>New Cost/ct</th>
              <th style={{ width: 80, textAlign: 'right' }}>% Change</th>
              <th style={{ width: 110, textAlign: 'right' }}>Change</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={12} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>No items match.</td></tr>
            )}
            {shown.map((i, idx) => {
              const c = calc(i);
              const e = edits[i.id];
              const ch = changeOf(i, c.np);
              const memo = onMemoCt(i);
              return (
                <tr key={i.id} className={e ? 'rvm-edited' : undefined}>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={ticked.has(i.id)} aria-label={`Tick ${i.code || i.shape}`}
                      onChange={() => setTicked(t => { const n = new Set(t); if (n.has(i.id)) n.delete(i.id); else n.add(i.id); return n; })} />
                  </td>
                  <td className="rvm-text rvm-num">{i.code || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td className="rvm-text rvm-num">{i.sku || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td className="rvm-text">{[i.shape, i.size].filter(Boolean).join(' ') || '—'}</td>
                  <td className="rvm-text">{i.color || '—'}</td>
                  <td className="rvm-text">{i.clarity || '—'}</td>
                  <td className="rvm-text">{i.lab || 'Loose'}</td>
                  <td className="rvm-text rvm-num" style={{ textAlign: 'right' }}>
                    {(i.carat ?? 0).toFixed(2)}{memo > 0 && <span className="rvm-memo"> ({memo.toFixed(2)} memo)</span>}
                  </td>
                  <td className="rvm-text rvm-num" style={{ textAlign: 'right' }}>{money(c.old)}</td>
                  <td style={{ background: c.np != null && c.np < 0 ? 'color-mix(in srgb, var(--danger) 12%, transparent)' : undefined }}>
                    <input type="number" step="0.01" className="grid-cell no-spinner" style={{ textAlign: 'right' }}
                      ref={nav.cellRef(idx, 0)} onKeyDown={nav.onCellKeyDown(idx, 0)}
                      value={e?.mode === 'cost' ? e.value : c.np != null ? c.np.toFixed(2) : ''}
                      placeholder={c.old.toFixed(2)}
                      onChange={ev => setEdit(i.id, 'cost', ev.target.value)} />
                  </td>
                  <td>
                    <input type="number" step="0.01" className="grid-cell no-spinner" style={{ textAlign: 'right' }}
                      ref={nav.cellRef(idx, 1)} onKeyDown={nav.onCellKeyDown(idx, 1)}
                      value={e?.mode === 'pct' ? e.value : pctText(c.pct)}
                      placeholder="0"
                      onChange={ev => setEdit(i.id, 'pct', ev.target.value)} />
                  </td>
                  <td className="rvm-text rvm-num" style={{ textAlign: 'right', color: ch < 0 ? 'var(--danger)' : ch > 0 ? 'var(--success)' : 'var(--text-3)' }}>
                    {Math.abs(ch) >= 0.005 ? `${ch > 0 ? '+' : ''}${money(ch)}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </Modal>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export default function StockRevaluationPage() {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [items, setItems] = useState<InventoryItem[]>([]);
  const [revals, setRevals] = useState<StockRevaluation[]>([]);
  const [loading, setLoading] = useState(true);

  const [dateKey, setDateKey] = useState<DateFilterKey>('this-month');
  const [searchQ, setSearchQ] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [showMany, setShowMany] = useState(false);
  const [savedNote, setSavedNote] = useState('');
  // Opened from the Inventory popup (?item=…): start with that stone picked.
  const [preItem, setPreItem] = useState<string | undefined>();
  useItemParam(id => { setPreItem(id); setShowAdd(true); });
  const [confirm, setConfirm] = useState<StockRevaluation | null>(null);
  const [busy, setBusy] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(collection(db, 'inventory'),
      snap => setItems(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem)).filter(i => i.status === 'in-stock')),
      () => {});
    const u2 = onSnapshot(collection(db, 'stockRevaluations'),
      snap => { setRevals(snap.docs.map(d => ({ id: d.id, ...d.data() } as StockRevaluation))); setLoading(false); },
      () => setLoading(false));
    return () => { u1(); u2(); };
  }, [business]);

  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const rows = useMemo(() => {
    if (!window) return [];
    const q = searchQ.trim().toLowerCase();
    return revals.filter(r => inWindow(r.date, window))
      .filter(r => !q || [r.revaluationNo, r.code, r.sku, r.batchNo].some(v => (v ?? '').toLowerCase().includes(q)))
      .sort((a, b) => b.date.localeCompare(a.date) || toMillis(b.createdAt) - toMillis(a.createdAt));
  }, [revals, window, searchQ]);

  useEffect(() => { setCurrentPage(1); }, [dateKey, customFrom, customTo, searchQ]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const kpi = useMemo(() => {
    const inW = window ? revals.filter(r => inWindow(r.date, window)) : [];
    const down = inW.filter(r => (r.deltaUSD ?? 0) < 0).reduce((s, r) => s + Math.abs(r.deltaUSD ?? 0), 0);
    const up = inW.filter(r => (r.deltaUSD ?? 0) > 0).reduce((s, r) => s + (r.deltaUSD ?? 0), 0);
    return { down, up, count: inW.length };
  }, [revals, window]);

  // Undoes one revaluation. `log` false for a whole batch (one line for it).
  async function deleteOne(r: StockRevaluation, log = true) {
    const db = getClientDb();
    if (!db || !business) return;
    const itemRef = doc(db, 'inventory', r.inventoryItemId);
    const skuRef = r.skuId ? doc(db, 'skus', r.skuId) : null;
    await runTransaction(db, async tx => {
      // Reads first (a transaction can't read after it writes).
      const is = await tx.get(itemRef);
      const ss = skuRef ? await tx.get(skuRef) : null;
      if (is.exists()) {
        tx.update(itemRef, {
          costUSD: round2(r.oldCostUSD ?? 0), costingUSD: round2(r.oldCostingUSD ?? 0),
          updatedAt: serverTimestamp(),
        });
      }
      if (skuRef) {
        if (ss?.exists()) {
          // Restore the SKU benchmark to its pre-revaluation figure. The
          // original added-value % isn't recoverable here (same limitation
          // as merge-undo), so it stays collapsed to 0.
          tx.update(skuRef, {
            currentAvgCostPerCt: round2(r.oldCostPerCt ?? 0),
            currentCostingPerCt: round2(r.oldCostPerCt ?? 0),
            currentAddedValuePercent: 0,
            updatedAt: serverTimestamp(),
          });
        }
        tx.delete(doc(db, 'skus', r.skuId!, 'history', `reval_${r.id}`));
      }
      if (r.code) tx.delete(doc(db, 'itemCodes', normalizeCode(r.code), 'history', `reval_${r.id}`));
      tx.delete(doc(db, 'journalEntries', entryId('stock_revaluation', r.id)));
      if (log) logActivity(tx, db, business.id, user, {
        module: 'revaluation', action: 'delete', recordId: r.id, recordNo: r.revaluationNo,
        itemCount: 1, carat: r.carat, amountUSD: r.deltaUSD,
        note: `Code ${r.code || '—'} · back to ${money(r.oldCostPerCt ?? 0)} per ct`,
      });
      tx.delete(doc(db, 'stockRevaluations', r.id));
    });
  }
  async function doDelete(r: StockRevaluation) {
    setBusy(true);
    try { await deleteOne(r); } finally { setBusy(false); setConfirm(null); }
  }
  // Every stone of a Revalue Many run, back to its old cost.
  const batchOf = (r: StockRevaluation) => (r.batchId ? revals.filter(x => x.batchId === r.batchId) : []);
  async function doDeleteBatch(r: StockRevaluation) {
    const db = getClientDb();
    if (!db || !business) return;
    const list = batchOf(r);
    setBusy(true);
    let done = 0;
    try {
      for (const x of list) { await deleteOne(x, false); done++; }
    } finally {
      if (done > 0) {
        await runTransaction(db, async tx => {
          logActivity(tx, db, business.id, user, {
            module: 'revaluation', action: 'delete', recordId: r.batchId!, recordNo: r.batchNo,
            itemCount: done, carat: round2(list.slice(0, done).reduce((s2, x) => s2 + (x.carat ?? 0), 0)),
            amountUSD: round2(list.slice(0, done).reduce((s2, x) => s2 + (x.deltaUSD ?? 0), 0)),
            note: `Revalue Many undone · ${done} of ${list.length} items`,
          });
        }).catch(() => {});
      }
      setBusy(false); setConfirm(null);
    }
  }

  return (
    <AppLayout
      title="Stock Revaluation"
      subtitle="Reset a lot's carrying value to a new cost per carat — its SKU benchmark moves with it and the change hits Profit."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Ref, batch, code, SKU…" label="Search revaluations" />
        <DateRangeButton
          value={dateKey} onChange={setDateKey}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
        {(searchQ || dateKey !== 'this-month') && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateKey('this-month'); }}>Clear</button>
        )}
      </>}
      headerActions={<>
        <Button variant="secondary" size="sm" onClick={() => { setSavedNote(''); setShowMany(true); }} disabled={!business || loading}
          style={{ height: 34, boxSizing: 'border-box' }}>Revalue Many</Button>
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setShowAdd(true)} disabled={!business}
          style={{ height: 34, boxSizing: 'border-box' }}>Revalue Stock</Button>
      </>}
    >
      {savedNote && (
        <div className="msg msg-note" style={{ marginBottom: 10 }}>
          <span style={{ flex: 1 }}>{savedNote}</span>
          <button type="button" className="line-item-link-btn" onClick={() => setSavedNote('')}>Dismiss</button>
        </div>
      )}
      <StatChips items={[
        { label: 'Written down', value: money(kpi.down), tone: kpi.down > 0 ? 'danger' : undefined },
        { label: 'Written up', value: money(kpi.up), tone: kpi.up > 0 ? 'success' : undefined },
        { label: 'Revaluations', value: String(kpi.count) },
      ]} />

      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 200, borderRadius: 8 }} /></div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No revaluations here</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Use “Revalue Stock” above to reprice a lot.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 120 }}>Ref</th>
                  <th>Item</th>
                  <th style={{ textAlign: 'right', width: 110 }}>Old /ct</th>
                  <th style={{ textAlign: 'right', width: 110 }}>New /ct</th>
                  <th style={{ textAlign: 'right', width: 120 }}>Change</th>
                  <th>Reason</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(r.date)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>
                      {r.revaluationNo}
                      {r.batchNo && (
                        <button type="button" className="rv-batch" title="Show this batch" onClick={() => setSearchQ(r.batchNo!)}>{r.batchNo}</button>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {r.code ? <span style={{ fontFamily: 'var(--font-num)' }}>{r.code}</span> : null}{r.code ? ' · ' : ''}{r.shape} {(r.carat ?? 0).toFixed(2)}ct
                    </td>
                    <td style={{ fontSize: 12, textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-2)' }}>{money(r.oldCostPerCt ?? 0)}</td>
                    <td style={{ fontSize: 12, textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-1)', fontWeight: 600 }}>{money(r.newCostPerCt ?? 0)}</td>
                    <td style={{ fontSize: 12, textAlign: 'right', fontFamily: 'var(--font-num)', color: (r.deltaUSD ?? 0) < 0 ? 'var(--danger)' : 'var(--success)' }}>
                      {(r.deltaUSD ?? 0) > 0 ? '+' : ''}{money(r.deltaUSD ?? 0)}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.reason}</td>
                    <td className="col-actions">
                      <DeleteIconButton onClick={() => setConfirm(r)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <Pagination
          current={currentPage} total={totalPages}
          totalItems={rows.length} pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      )}

      {showMany && (
        <BulkRevalueModal items={items} onClose={() => setShowMany(false)}
          onSaved={(no, count) => { setSavedNote(`${no}: ${count} item${count === 1 ? '' : 's'} revalued.`); setSearchQ(no); }} />
      )}
      {showAdd && <RevalueModal items={items} initialItemId={preItem} onClose={() => { setShowAdd(false); setPreItem(undefined); }} />}
      {confirm && confirm.batchId && batchOf(confirm).length > 1 && (
        <Modal open onClose={() => !busy && setConfirm(null)} title="Delete this revaluation?" size="sm"
          footer={<>
            <Button variant="secondary" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
            <Button variant="secondary" onClick={() => doDelete(confirm)} disabled={busy}>This item only</Button>
            <Button variant="danger" onClick={() => doDeleteBatch(confirm)} loading={busy}>
              Whole batch ({batchOf(confirm).length})
            </Button>
          </>}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)' }}>
            {confirm.revaluationNo} is part of {confirm.batchNo} ({batchOf(confirm).length} items). Undo just this
            item, or the whole batch? Each item goes back to its old cost and the Profit effect is reversed.
          </p>
        </Modal>
      )}
      {confirm && !(confirm.batchId && batchOf(confirm).length > 1) && (
        <ConfirmModal
          open
          title="Delete this revaluation?"
          message={`${confirm.revaluationNo} will be removed — the item returns to ${money(confirm.oldCostUSD ?? 0)} and its Profit effect is reversed.`}
          confirmLabel="Delete"
          loading={busy}
          onConfirm={() => doDelete(confirm)}
          onClose={() => setConfirm(null)}
        />
      )}
    </AppLayout>
  );
}
